import React, { useState, useRef, useEffect } from "react";
import EnhancedLatexRenderer from "./EnhancedLatexRenderer";

/**
 * Component to visualize product attribute data in a table format
 * Shows distribution bars for each product across different attributes
 */
export function AttributeVisualization({ vizData, language = "en" }) {
  // State to track which products are visible in the distribution
  // Must be declared before any early returns
  const [visibleProducts, setVisibleProducts] = useState({});

  // Early return check - do this AFTER all hooks
  const hasData = vizData && vizData.scopes && vizData.scopes.length > 0;

  const scope = hasData ? vizData.scopes[0] : null;
  const models = hasData ? vizData.models || [] : [];

  // Initialize visibleProducts if models have changed
  // This hook must be called before any conditional returns
  // Stable key so the effect only re-runs when the set of models changes,
  // not on every render (models is rebuilt from props each time).
  const modelsKey = models.join(",");

  useEffect(() => {
    if (!modelsKey) return;
    setVisibleProducts(
      modelsKey
        .split(",")
        .reduce((acc, model) => ({ ...acc, [model]: true }), {}),
    );
  }, [modelsKey]);

  // Now we can do the early return AFTER all hooks
  if (!hasData) {
    return null;
  }

  // Color palette for up to 6 products
  const productColors = [
    { primary: "#f97316", light: "#fb923c", ring: "#fdba74" }, // Orange
    { primary: "#8b5cf6", light: "#a78bfa", ring: "#c4b5fd" }, // Violet
    { primary: "#ec4899", light: "#f472b6", ring: "#f9a8d4" }, // Pink
    { primary: "#06b6d4", light: "#22d3ee", ring: "#67e8f9" }, // Cyan
    { primary: "#10b981", light: "#34d399", ring: "#6ee7b7" }, // Emerald
    { primary: "#f59e0b", light: "#fbbf24", ring: "#fcd34d" }, // Amber
  ];

  // Assign colors to models
  const modelColors = models.reduce((acc, model, idx) => {
    acc[model] = productColors[idx % productColors.length];
    return acc;
  }, {});

  // Toggle product visibility
  const toggleProductVisibility = (model) => {
    setVisibleProducts((prev) => ({
      ...prev,
      [model]: !prev[model],
    }));
  };

  // Translations
  const t = {
    productComparison: language === "zh" ? "產品比較" : "Product Comparison",
    comparing: language === "zh" ? "比較中" : "Comparing",
    attribute: language === "zh" ? "屬性" : "Attribute",
  };

  return (
    <div className="relative overflow-x-auto my-4 rounded-xl shadow-lg mx-auto w-full bg-gray-100">
      {/* Product Toggle Controls */}
      <div className="rounded-t-xl px-4 py-3 border border-gray-300 bg-gray-50">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-gray-600 font-semibold">
            Show in Distribution:
          </span>
          {models.map((model, idx) => {
            const color = modelColors[model];
            const isVisible = visibleProducts[model];
            return (
              <button
                key={model}
                onClick={() => toggleProductVisibility(model)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
                  isVisible
                    ? "bg-white border-gray-300"
                    : "bg-gray-100 border-gray-200 opacity-50"
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: isVisible ? color.primary : "#4b5563",
                  }}
                />
                <span
                  className={`text-xs font-medium ${
                    isVisible
                      ? "text-gray-800"
                      : "text-gray-400"
                  }`}
                >
                  {model}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table View - Attributes as Rows, Products as Columns, Distribution Bar in Last Column */}
      <div className="overflow-x-auto overflow-y-visible rounded-b-xl">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-gray-300/30">
              <th className="w-[15%] bg-orange-200 border-r border-gray-300/30 text-left py-3 px-4 text-lg text-gray-800 font-semibold sticky left-0">
                {t.attribute}
              </th>
              {models.map((model, idx) => {
                const color = modelColors[model];
                return (
                  <th
                    key={idx}
                    className={`w-[${
                      45 / models.length
                    }%] text-center py-3 px-4 text-gray-800 font-semibold border-r border-gray-300/30 text-lg`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{
                          backgroundColor: color.primary,
                        }}
                      />
                      <span>{model}</span>
                    </div>
                  </th>
                );
              })}
              <th className="w-[40%] text-center py-3 px-4 text-gray-800 font-semibold text-lg">
                <div className="w-full h-full flex flex-row gap-4 items-center justify-end pr-8">
                  <div className="flex flex-col items-end gap-2">
                    <div className="w-12 h-4 bg-gray-300 border border-gray-400 rounded-sm"></div>
                    <div className="w-8 h-4 bg-gray-100/40 border border-gray-400 rounded-sm"></div>
                  </div>
                  <div className="flex flex-col items-start gap-2 text-xs">
                    <div>Typical Range </div>
                    <div>All range (100%)</div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {scope.attributes.map((attr, idx) => (
              <AttributeRow
                key={idx}
                attribute={attr}
                models={models}
                modelColors={modelColors}
                visibleProducts={visibleProducts}
                language={language}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Individual attribute row component with bars for each product
 */
function AttributeRow({
  attribute,
  models,
  modelColors,
  visibleProducts,
  language = "en",
}) {
  const { attr_name, unit, min_val, max_val, quantiles_0_100, markers } =
    attribute;
  // State to track if hovering over the distribution bar
  const [isHoveringBar, setIsHoveringBar] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const barRef = useRef(null);

  // Skip if no valid data
  if (!quantiles_0_100 || quantiles_0_100.length === 0) return null;

  // Translations
  const t = {
    typicalRange: language === "zh" ? "典型範圍" : "Typical Range",
    median: language === "zh" ? "中位數" : "Median",
    percentile: language === "zh" ? "百分位" : "th percentile",
    noData: language === "zh" ? "無數據" : "No data",
  };

  // Helper function to format values
  const formatValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (value >= 1e12) return `${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    if (value < 0.01 && value > 0) return value.toExponential(2);
    return value.toFixed(2);
  };

  // Calculate actual min and max values
  const allValues = [
    min_val,
    max_val,
    ...quantiles_0_100.filter((v) => v !== null && v !== undefined),
  ];

  // Add marker values
  if (markers) {
    markers.forEach((marker) => {
      if (marker.type === "scalar" && marker.value !== null) {
        allValues.push(marker.value);
      } else if (marker.type === "band") {
        if (marker.low !== null) allValues.push(marker.low);
        if (marker.high !== null) allValues.push(marker.high);
      }
    });
  }

  const actualMin = Math.min(...allValues.filter((v) => !isNaN(v)));
  const actualMax = Math.max(...allValues.filter((v) => !isNaN(v)));

  // Calculate position
  const getMarkerPosition = (value) => {
    if (actualMin === actualMax) return 50;
    return ((value - actualMin) / (actualMax - actualMin)) * 100;
  };

  // Get quartiles
  const q25 = quantiles_0_100[25] || actualMin;
  const q75 = quantiles_0_100[75] || actualMax;

  // Handle mouse enter to calculate tooltip position
  const handleMouseEnter = () => {
    if (barRef.current) {
      const rect = barRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    }
    setIsHoveringBar(true);
  };

  return (
    <tr className="border-b border-gray-300/30 hover:bg-orange-50/50 transition-colors">
      {/* Attribute Name Column - Sticky */}
      <td className="py-3 px-4 text-gray-800 font-semibold sticky left-0 border-r border-gray-300/30 bg-orange-200 z-40">
        <span className="capitalize">{attr_name}</span>
        {unit && (
          <span className="text-gray-600 text-[10px] ml-1 font-normal">
            <EnhancedLatexRenderer text={unit} />
          </span>
        )}
      </td>

      {/* Product Value Columns - Just show the values */}
      {models.map((model, modelIdx) => {
        const marker = markers?.find((m) => m.model === model);

        return (
          <td
            key={modelIdx}
            className="py-3 px-4 border-r border-gray-300/30 text-center bg-white/50"
          >
            {marker ? (
              marker.type === "scalar" ? (
                <div className="flex items-center justify-center gap-2">
                  {/* <div
                                        className="w-2 h-2 rounded-full"
                                        style={{
                                            backgroundColor: color.primary,
                                        }}
                                    /> */}
                  <div className="text-orange-600 font-semibold text-base">
                    {formatValue(marker.value)}
                    {unit && (
                      <span className="text-gray-500 text-xs ml-1">
                        <EnhancedLatexRenderer text={unit} />
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {/* <div
                                        className="w-2 h-2 rounded-full"
                                        style={{
                                            backgroundColor: color.primary,
                                        }}
                                    /> */}
                  <div className="text-orange-600 font-semibold text-sm">
                    {formatValue(marker.low)} - {formatValue(marker.high)}
                    {unit && (
                      <span className="text-gray-500 text-xs ml-1">
                        <EnhancedLatexRenderer text={unit} />
                      </span>
                    )}
                  </div>
                </div>
              )
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </td>
        );
      })}

      {/* Distribution Bar Column - Shows all products on one bar */}
      <td className="py-3 px-4 relative bg-white/50">
        <div className="relative">
          {/* Distribution Bar */}
          <div className="flex flex-row items-center justify-center gap-2 h-6">
            <div className="text-[10px] text-gray-500 font-semibold w-12 text-right">
              {formatValue(actualMin)}
            </div>
            <div
              ref={barRef}
              className="flex-1 relative h-6 bg-gray-100/40 rounded border border-gray-400 min-w-[200px] cursor-pointer"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setIsHoveringBar(false)}
            >
              {/* Typical Range (IQR) - Blue */}
              <div
                className="absolute top-0 bottom-0 rounded bg-gray-300 z-10"
                style={{
                  left: `${getMarkerPosition(q25)}%`,
                  right: `${100 - getMarkerPosition(q75)}%`,
                }}
              />

              {/* Median line - Green */}
              {/* <div
                                className="absolute top-0 bottom-0 w-0.5 bg-green-400/60 z-20"
                                style={{
                                    left: `${getMarkerPosition(q50)}%`,
                                }}
                            /> */}

              {/* Product Markers - All on the same bar */}
              {markers?.map((marker, idx) => {
                const color = modelColors[marker.model];
                const isVisible = visibleProducts[marker.model];

                if (!isVisible) return null; // Skip if not visible

                if (marker.type === "scalar") {
                  return (
                    <div
                      key={idx}
                      className="absolute top-0 bottom-0 flex items-center z-30 pointer-events-none"
                      style={{
                        left: `${getMarkerPosition(marker.value)}%`,
                        transform: "translateX(-50%)",
                      }}
                    >
                      <div
                        className="w-1 h-full shadow-lg"
                        style={{
                          backgroundColor: color.primary,
                          boxShadow: `0 0 8px ${color.light}40`,
                        }}
                      />
                    </div>
                  );
                } else if (marker.type === "band") {
                  return (
                    <div
                      key={idx}
                      className="absolute top-0 bottom-0 z-30 pointer-events-none"
                      style={{
                        left: `${getMarkerPosition(marker.low)}%`,
                        right: `${100 - getMarkerPosition(marker.high)}%`,
                        backgroundColor: `${color.primary}50`,
                        borderLeft: `2px solid ${color.primary}`,
                        borderRight: `2px solid ${color.primary}`,
                      }}
                    />
                  );
                }
                return null;
              })}
            </div>
            <div className="text-[10px] text-gray-500 font-semibold w-12 text-left">
              {formatValue(actualMax)}
            </div>
          </div>

          {/* Legend below the bar */}
          <div className="flex items-center justify-center gap-4 mt-2 text-[10px] flex-wrap">
            {/* <div className="flex items-center gap-1">
                            <div className="w-3 h-3  bg-gray-100/40 rounded border border-gray-500/60"></div>
                            <span className="text-gray-400">
                                {t.typicalRange}
                            </span>
                        </div> */}
            {/* <div className="flex items-center gap-1">
                            <div className="w-3 h-0.5 bg-green-400/60"></div>
                            <span className="text-gray-400">{t.median}</span>
                        </div> */}
            {models.map((model) => {
              const color = modelColors[model];
              const isVisible = visibleProducts[model];
              if (!isVisible) return null;
              return (
                <div key={model} className="flex items-center gap-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: color.primary,
                    }}
                  />
                  <span className="text-gray-600">
                    {model}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Unified Tooltip - Shows when hovering anywhere on the distribution bar */}
          {isHoveringBar && (
            <div
              className="fixed pointer-events-none z-[9999]"
              style={{
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                transform: "translate(-50%, calc(-100% - 10px))",
              }}
            >
              <div
                className="bg-gray-800 text-white text-xs rounded-lg py-2.5 px-3.5 border shadow-2xl"
                style={{
                  borderColor: "#3b82f6",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 0 10px rgba(59, 130, 246, 0.4)",
                  minWidth: "200px",
                  maxWidth: "350px",
                }}
              >
                {/* Attribute Name Header */}
                <div className="font-semibold text-gray-100 mb-2 pb-2 border-b border-gray-600">
                  {attr_name}
                  {unit && (
                    <span className="text-gray-400 text-[10px] ml-1">
                      <EnhancedLatexRenderer text={unit} />
                    </span>
                  )}
                </div>

                {/* Typical Range Information */}
                <div className="mb-2 space-y-1 whitespace-normal">
                  <div className="text-gray-300 text-[10px] font-semibold mb-1">
                    {t.typicalRange}:
                  </div>
                  <div className="flex items-center gap-2 text-blue-300">
                    <div className="w-3 h-2 bg-blue-500/40 rounded border border-blue-500/60 flex-shrink-0"></div>
                    <span className="text-xs">
                      {formatValue(q25)} - {formatValue(q75)}
                    </span>
                  </div>
                  {/* <div className="flex items-center gap-2 text-green-300">
                                        <div className="w-3 h-0.5 bg-green-400/60 flex-shrink-0"></div>
                                        <span className="text-xs">
                                            {t.median}: {formatValue(q50)}
                                        </span>
                                    </div> */}
                </div>

                {/* Product Values - Show all visible products */}
                {markers && markers.length > 0 && (
                  <div className="pt-2 border-t border-gray-600 space-y-1.5 whitespace-normal">
                    <div className="text-gray-300 text-[10px] font-semibold mb-1">
                      Product Values:
                    </div>
                    {markers
                      .filter((m) => visibleProducts[m.model])
                      .map((marker, idx) => {
                        const color = modelColors[marker.model];
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor: color.primary,
                              }}
                            />
                            <span className="text-gray-200 text-xs font-medium">
                              {marker.model}:
                            </span>
                            <span
                              className="text-xs font-semibold"
                              style={{
                                color: color.light,
                              }}
                            >
                              {marker.type === "scalar"
                                ? formatValue(marker.value)
                                : `${formatValue(marker.low)} - ${formatValue(
                                    marker.high,
                                  )}`}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export default AttributeVisualization;
